import React from 'react';
import PropTypes from 'prop-types';
import Skeleton from './Skeleton';
import SkeletonGroup from './SkeletonGroup';
import './Skeleton.scss';

/**
 * AvatarSkeleton component - Use for user/profile items.
 *
 * This component creates a skeleton layout that mimics typical user profile
 * displays with a circular avatar placeholder and two text lines for name
 * and subtitle. It's perfect for loading states of user lists, profile cards,
 * or any UI that displays user information.
 *
 * @component
 * @example
 * // Basic avatar skeleton
 * <AvatarSkeleton />
 *
 * @example
 * // Custom avatar size
 * <AvatarSkeleton avatarSize="48px" />
 *
 * @example
 * // Three lines instead of two
 * <AvatarSkeleton textLines={3} />
 *
 * @example
 * // Horizontal layout with custom spacing
 * <AvatarSkeleton
 *   direction="horizontal"
 *   spacing="md"
 *   avatarSize="40px"
 * />
 */
// Design token defaults (matching _variables.scss)
const AVATAR_DEFAULTS = {
  avatarSize: '40px', // $skeleton-avatar-size-md
  textLineHeight: '14px', // $skeleton-text-height-md
  nameWidth: '120px', // $skeleton-text-width-name
  subtitleWidth: '80px', // $skeleton-text-width-subtitle
  borderRadius: '4px', // $skeleton-border-radius-sm
};

const AvatarSkeleton = ({
  avatarSize = AVATAR_DEFAULTS.avatarSize,
  textLines = 2,
  direction = 'horizontal',
  spacing = 'sm',
  className = '',
  ...props
}) => {
  const avatar = (
    <Skeleton
      width={avatarSize}
      height={avatarSize}
      borderRadius='50%'
      className='avatar-skeleton-avatar'
    />
  );

  const textContent = (
    <SkeletonGroup
      direction='vertical'
      spacing='xs'
      className='avatar-skeleton-text'
    >
      {Array.from({ length: textLines }, (_, index) => (
        <Skeleton
          key={index}
          width={
            index === 0
              ? AVATAR_DEFAULTS.nameWidth
              : AVATAR_DEFAULTS.subtitleWidth
          }
          height={AVATAR_DEFAULTS.textLineHeight}
          borderRadius={AVATAR_DEFAULTS.borderRadius}
          className='avatar-skeleton-text-line'
        />
      ))}
    </SkeletonGroup>
  );

  return (
    <SkeletonGroup
      direction={direction}
      spacing={spacing}
      className={`avatar-skeleton avatar-skeleton--${direction} ${className}`}
      {...props}
    >
      {avatar}
      {textContent}
    </SkeletonGroup>
  );
};

AvatarSkeleton.propTypes = {
  /** Size of the circular avatar */
  avatarSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Number of text lines (1-3 recommended) */
  textLines: PropTypes.number,

  /** Layout direction (horizontal or vertical) */
  direction: PropTypes.oneOf(['horizontal', 'vertical']),

  /** Spacing between avatar and text (xs, sm, md, lg, xl) */
  spacing: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),

  /** Additional CSS classes */
  className: PropTypes.string,
};

export default AvatarSkeleton;
